// thumbP (design_logic.js): a layout template painted with a palette in a colour mode, mirrored on
// request. The result is static markup built only from the verbatim templates, validated hex
// colours and an HTML-escaped caption, so it is safe to set as innerHTML. Memoised like the design's THC.
import { hexRgb, modeColors, safePalette, type Palette } from './palettes';
import { isColorMode, isLayout, type ColorMode, type Layout } from './taxonomy';
import { TEMPLATES } from './templates';

export type ThumbSpec = { layout: Layout | string; mode?: ColorMode | string; mirrored?: boolean };

/** The design's caption of the empty custom block; screens pass the translated one. */
export const CUSTOM_LABEL = 'Свой блок';

const MAX_CACHE = 4000;
const cache = new Map<string, string>();

const escapeHtml = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string);

function key(spec: ThumbSpec, p: Palette, label: string): string {
  return `${spec.layout}|${spec.mode ?? 'light'}|${spec.mirrored ? 1 : 0}|${p.bg}${p.ink}${p.acc}${p.ph}${p.ph2}${p.lt}|${label}`;
}

/**
 * The thumbnail markup. An unknown layout renders the custom block (design: TOK[lay] || TOK.custom),
 * an unknown mode renders light, an invalid palette the design's brand palette.
 */
export function thumbHtml(spec: ThumbSpec, palette?: Palette | null, label: string = CUSTOM_LABEL): string {
  const p = safePalette(palette);
  const k = key(spec, p, label);
  const hit = cache.get(k);
  if (hit !== undefined) return hit;
  const layout: Layout = isLayout(spec.layout) ? spec.layout : 'custom';
  const mode: ColorMode = isColorMode(spec.mode) ? spec.mode : 'light';
  const c = modeColors(p, mode);
  const r = (h: string) => hexRgb(h).join(',');
  let html: string = TEMPLATES[layout];
  html = html
    .split('%BGR%').join(r(c.bg))
    .split('%INKR%').join(r(c.ink))
    .split('%ACCR%').join(r(c.acc))
    .split('%LTR%').join(r(c.lt))
    .split('%BG%').join(c.bg)
    .split('%INK%').join(c.ink)
    .split('%ACC%').join(c.acc)
    .split('%LT%').join(c.lt)
    .split('%PH2%').join(c.ph2)
    .split('%PH%').join(c.ph)
    .split('%LABEL%').join(escapeHtml(label));
  if (spec.mirrored) html = `<div style="width:100%;height:100%;transform:scaleX(-1);">${html}</div>`;
  if (cache.size >= MAX_CACHE) cache.clear();
  cache.set(k, html);
  return html;
}
